import { first } from 'lodash';
import {
  IStereoVideoDetection,
  IStereoVideoIndexedPoint,
} from '../interfaces/i-stereo-video';
import { IStrikeZone } from '../interfaces/i-strike-zone';
import { RAD_FULL_ROTATION } from './math.utilities';

interface IPixelCoordinate {
  x: number;
  y: number;
}

// multipy a ft measurement by the appropriate factor to get pixels in frame
interface IFtToPixelScaling {
  x: number;
  // use z instead of y because this is in the real world frame of reference
  z: number;
}

export class ReplayCanvas {
  // for debugging, e.g. provide the origin to confirm the dot sits on where home plate is
  static drawDebugDot = (
    ctx: CanvasRenderingContext2D,
    coord: IPixelCoordinate, // pixel coordinate
    options: {
      color: string;
    }
  ) => {
    try {
      ctx.fillStyle = options.color;

      ctx.beginPath();
      ctx.ellipse(coord.x, coord.y, 8, 8, 0, 0, RAD_FULL_ROTATION);

      ctx.fill();
    } catch (e) {
      console.error('drawDebugDot', e);
    }
  };

  static drawStrikeZone = (
    ctx: CanvasRenderingContext2D,
    scaling: IFtToPixelScaling,
    origin: IPixelCoordinate,
    szDef: IStrikeZone,
    options: {
      lineWidth: number;
      color: string;
    }
  ) => {
    try {
      ctx.lineWidth = options.lineWidth;
      ctx.strokeStyle = options.color;

      // ? roundRect is not supported below TS 5.4
      ctx.strokeRect(
        // starting location of the rectangle
        origin.x - scaling.x * Math.abs(szDef.left_ft),
        origin.y - scaling.z * szDef.top_ft,

        // size of the rectangle
        scaling.x * Math.abs(szDef.right_ft - szDef.left_ft),
        scaling.z * Math.abs(szDef.top_ft - szDef.bottom_ft)
      );

      ctx.stroke();
    } catch (e) {
      console.error('drawStrikeZone', e);
    }
  };

  static drawPlateLoc = (
    ctx: CanvasRenderingContext2D,
    ball_crossing: IStereoVideoDetection,
    options: {
      lineWidth: number;
      outlineColor: string;
      fillColor: string;
    }
  ) => {
    try {
      ctx.lineWidth = options.lineWidth;
      ctx.strokeStyle = options.outlineColor;
      ctx.fillStyle = options.fillColor;

      ctx.beginPath();
      ctx.ellipse(
        ball_crossing.x_px,
        ball_crossing.y_px,
        ball_crossing.r_px,
        ball_crossing.r_px,
        0,
        0,
        RAD_FULL_ROTATION
      );
      ctx.fill();
      ctx.stroke();
    } catch (e) {
      console.error('drawPlateLoc', e);
    }
  };

  static drawDetection = (
    ctx: CanvasRenderingContext2D,
    detection: IStereoVideoDetection,
    options: {
      shape: 'circle' | 'rect';
      lineWidth: number;
      color: string;
    }
  ) => {
    try {
      switch (options.shape) {
        case 'circle': {
          ctx.lineWidth = options.lineWidth;
          ctx.strokeStyle = options.color;

          ctx.beginPath();
          ctx.ellipse(
            detection.x_px,
            detection.y_px,
            detection.r_px,
            detection.r_px,
            0,
            0,
            RAD_FULL_ROTATION
          );
          ctx.stroke();
          return;
        }

        case 'rect': {
          ctx.lineWidth = options.lineWidth;
          ctx.strokeStyle = options.color;

          ctx.strokeRect(
            // top left corner of square
            detection.x_px - detection.r_px,
            detection.y_px - detection.r_px,

            // side length is 2x radius
            detection.r_px * 2,
            detection.r_px * 2
          );
          return;
        }

        default: {
          return;
        }
      }
    } catch (e) {
      console.error('drawDetection', e);
    }
  };

  static drawTracerPoints = (
    ctx: CanvasRenderingContext2D,
    points: IStereoVideoIndexedPoint[],
    options: {
      lineWidth: number;
      lineColor: string;
      shadowWidth: number;
      shadowColor: string;
    }
  ) => {
    try {
      ctx.beginPath();

      ctx.lineWidth = options.lineWidth;
      ctx.shadowBlur = options.shadowWidth;
      ctx.shadowColor = options.shadowColor;
      ctx.strokeStyle = options.lineColor;

      let prevPt = first(points);

      if (!prevPt) {
        // can't start w/o a first point
        return;
      }

      for (const point of points.filter((_, i) => i > 0)) {
        ctx.moveTo(prevPt.x_px, prevPt.y_px);
        ctx.lineTo(point.x_px, point.y_px);
        ctx.stroke();
        prevPt = point;
      }

      ctx.closePath();
    } catch (e) {
      console.error('drawTracerPoints', e);
    }
  };
}
